import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { errorKey } from '../api/client';

export interface ReportColumn<TRow> {
  id: string;
  label: string;
  render: (row: TRow) => ReactNode;
  /** The value sorted on, when it is not what `render` shows. */
  sortValue?: (row: TRow) => string | number | null;
  /** Rendered once under the column, from every row that passed the filter. */
  total?: (rows: TRow[]) => ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
}

interface ReportTableProps<TRow> {
  columns: ReportColumn<TRow>[];
  rows: TRow[];
  rowKey: (row: TRow) => string;
  loading?: boolean;
  error?: unknown;
  /** Set when the server had to cut the report short. */
  truncated?: boolean;
  /** The default sort column; descending, since reports lead with the largest. */
  defaultSortBy?: string;
  onRowClick?: (row: TRow) => void;
  /** Rendered above the table: filters, an export button, a note. */
  toolbar?: ReactNode;
  /** Explains a figure the columns cannot, shown under the toolbar. */
  note?: string;
}

/**
 * A report, as a table.
 *
 * Deliberately not `DataTable`: that one is built around a server-paginated
 * list with a search box, and a report is a whole grouped set that arrives in
 * one response (see the shared report schemas). So sorting and paging happen
 * here, in the browser, over rows that are already in memory — and the footer
 * can total a column honestly, because every row is present rather than one
 * page of them.
 */
export function ReportTable<TRow>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  truncated,
  defaultSortBy,
  onRowClick,
  toolbar,
  note,
}: ReportTableProps<TRow>) {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<string | undefined>(defaultSortBy);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const sorted = useMemo(() => {
    const column = columns.find((candidate) => candidate.id === sortBy);
    if (!column?.sortValue) return rows;

    const direction = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const left = column.sortValue!(a);
      const right = column.sortValue!(b);

      // Nulls last whichever way the column is sorted: an empty cell is not a
      // small value, and burying them under the numbers is what a reader wants.
      if (left === null || left === '') return 1;
      if (right === null || right === '') return -1;

      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * direction;
      }
      return String(left).localeCompare(String(right)) * direction;
    });
  }, [columns, rows, sortBy, sortDir]);

  const visible = sorted.slice(page * pageSize, page * pageSize + pageSize);
  const hasTotals = columns.some((column) => column.total);

  function sort(id: string): void {
    if (id === sortBy) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(id);
      setSortDir('desc');
    }
    setPage(0);
  }

  return (
    <Stack spacing={1.5}>
      {toolbar}

      {note && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {note}
        </Typography>
      )}

      {truncated && <Alert severity="warning">{t('reports.truncated', { count: rows.length })}</Alert>}
      {error ? <Alert severity="error">{t(errorKey(error))}</Alert> : null}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{ width: column.width }}
                  sortDirection={sortBy === column.id ? sortDir : false}
                >
                  {column.sortValue ? (
                    <TableSortLabel
                      active={sortBy === column.id}
                      direction={sortBy === column.id ? sortDir : 'desc'}
                      onClick={() => sort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            )}

            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('common.noData')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {visible.map((row) => (
              <TableRow
                key={rowKey(row)}
                hover={Boolean(onRowClick)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {columns.map((column) => (
                  <TableCell key={column.id} align={column.align}>
                    {column.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>

          {hasTotals && rows.length > 0 && (
            <TableFooter>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{ fontWeight: 700, color: 'text.primary' }}
                  >
                    {/* The total is over every row, not the page: a page's
                        subtotal is a figure nobody asked for. */}
                    {column.total ? column.total(rows) : index === 0 ? t('reports.total') : null}
                  </TableCell>
                ))}
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => {
            setPageSize(Number.parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) =>
            t('common.displayedRows', { from, to, count })
          }
        />
      </Box>
    </Stack>
  );
}
